import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ConstareAIPanel,
  ConstareAIPanelProvider,
} from '../components/ConstareAIPanel';

/**
 * Lesson 3.2 visual preview at /preview/lesson/3.2
 * "The research step: finding what is true"
 */

export default function LessonPreviewPage3_2() {
  return (
    <ConstareAIPanelProvider>
      <LessonPage />
    </ConstareAIPanelProvider>
  );
}

function LessonPage() {
  const totalSections = 6;
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const ids = [
      'sec-cold-open',
      'sec-from-last-time',
      'sec-teaching',
      'sec-your-turn',
      'sec-quick-check',
      'sec-lock-card',
    ];
    const observer = new IntersectionObserver(
      (entries) => {
        const vis = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (vis[0]) {
          const idx = ids.indexOf(vis[0].target.id);
          if (idx >= 0) setActiveIndex(idx);
        }
      },
      { rootMargin: '-30% 0px -55% 0px', threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const progressPct = useMemo(
    () => Math.round(((activeIndex + 1) / totalSections) * 100),
    [activeIndex]
  );

  return (
    <div className="relative -mx-6 -my-10 min-h-screen bg-ink">
      <div className="sticky top-0 z-20 border-b border-line bg-ink/95 backdrop-blur">
        <div className="mx-auto flex max-w-content items-center gap-6 px-6 py-3">
          <Link to="/dashboard" className="text-xs text-muted hover:text-text">
            ← Home
          </Link>
          <div className="hidden flex-1 sm:block">
            <div className="flex items-center gap-3">
              <div className="h-1 flex-1 overflow-hidden rounded-full bg-elevated">
                <div
                  className="h-full bg-amber transition-all duration-300"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted">
                {activeIndex + 1} / {totalSections}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto grid max-w-content gap-8 px-6 py-10 lg:grid-cols-[200px_minmax(0,1fr)_380px]">
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <SectionNav activeIndex={activeIndex} />
          </div>
        </aside>
        <article className="min-w-0">
          <LessonHeader />
          <ColdOpen />
          <FromLastTime />
          <Teaching />
          <YourTurn />
          <QuickCheck />
          <LockCard />
          <NextUp />
        </article>
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <ConstareAIPanel />
          </div>
        </aside>
      </div>
    </div>
  );
}

function LessonHeader() {
  return (
    <header className="mb-12">
      <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-amber">
        Week 3 · Building with AI · Lesson 3.2
      </p>
      <h1 className="mt-3 text-4xl font-semibold leading-[1.1] tracking-tight text-text">
        The research step: finding what is true
      </h1>
      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted">
        <span className="flex items-center gap-1.5">
          <ClockIcon /> 40 min
        </span>
        <span className="h-3 w-px bg-line" />
        <span>Threads: Vocabulary · Judgment · Character</span>
        <span className="h-3 w-px bg-line" />
        <span className="text-amber">4 source-spotting drills</span>
      </div>
    </header>
  );
}

function ColdOpen() {
  return (
    <Section
      id="sec-cold-open"
      label="Cold open"
      eyebrow="Four citations. Three real. One invented. The teacher checked the wrong one."
      accent="amber"
    >
      <p>
        Real story shape. AP US History paper. Five pages. The student asks AI
        for help finding sources on the role of women in the Civil Rights
        movement. AI gives them four citations, all formatted perfectly, all
        with author, year, journal, page numbers. The student reads them in
        the AI summary, picks the strongest quotes, builds a thesis around
        them, submits.
      </p>
      <CitationGrid />
      <p className="mt-4">
        Three of the citations are real papers and real quotes. One is
        invented. The author exists. The journal exists. The year is plausible.
        The paper does not exist. The student never opened any of them. The
        teacher spot-checked one citation, on a hunch, and pulled the
        invented one. The whole paper got a zero for academic dishonesty.
      </p>
      <p className="text-text">
        The student did not lie. The AI did. The student did not check. That
        is the lesson. Research is not asking AI for facts. Research is asking
        AI where to look, then reading what is there.
      </p>
    </Section>
  );
}

function FromLastTime() {
  return (
    <Section
      id="sec-from-last-time"
      label="From last time · Lesson 3.1"
      eyebrow="Three questions on chaining. No looking back."
      accent="blue"
    >
      <ol className="space-y-3 text-text">
        <li>
          <span className="font-mono text-amber">01.</span> Define chaining
          in one sentence.
        </li>
        <li>
          <span className="font-mono text-amber">02.</span> Name the four
          moves in the shape of a good chain, in order.
        </li>
        <li>
          <span className="font-mono text-amber">03.</span> What are the three
          handoff rules between steps in a chain?
        </li>
      </ol>
      <p className="mt-5 text-sm text-muted">
        If any of those felt fuzzy, run 3.1 again before you start this
        lesson. Today is the first link in the chain. If you do not nail
        research, every step downstream is built on something you cannot
        defend.
      </p>
    </Section>
  );
}

function Teaching() {
  return (
    <Section
      id="sec-teaching"
      label="Teaching · Research, the real version"
      eyebrow="What research actually is. What AI knows. What AI fakes. How to tell."
    >
      <Subhead n="1" title="What research actually is" />
      <p>
        Most students think research is collecting answers. You ask, AI tells
        you, you take notes on what AI said, you cite AI. That is not
        research. That is dictation. The teacher reading your paper does not
        care what the AI knows. They care what you can defend if they ask
        you on Monday.
      </p>
      <p>
        Research is collecting <span className="text-amber">where the
        answers live</span> so you can read the answers yourself. AI is a
        librarian. A librarian knows where things are. A librarian does not
        write the books. If you walk out of the library with only a
        librarian&apos;s summary and no book, you did not research.
      </p>
      <LibrarianFrame />

      <Subhead n="2" title="The thing you have to name" />
      <p>
        There is a word for the real thing you are looking for in research.
        Most students never learn the word. They write papers their whole
        senior year without it. With the word, the rest of this lesson
        clicks.
      </p>
      <VocabCard
        term="Primary source"
        definition="The original place a fact lives. The paper that ran the study. The database with the numbers. The press release. The person who said the thing. The court ruling. Not someone summarizing it. Not AI explaining it. The original."
        analogy="If your friend tells you what their friend said about you, that is hearsay. If you read the text message your friend got, that is the primary source. Same words sometimes. Different reliability completely."
        hook="The original. Not a summary of the original. Not a summary of a summary."
        matters="A teacher who asks where you got that wants a primary source. A college admissions officer who asks about your research wants a primary source. AI is never a primary source. Even when it sounds like one. Even when it gives you a citation. The primary source is the thing the citation points to."
      />

      <div className="mt-6 rounded-lg border border-line bg-surface p-5">
        <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
          Also worth knowing
        </p>
        <h4 className="mt-1 text-base font-semibold text-text">Retrieval-augmented generation (RAG)</h4>
        <p className="mt-3 text-text/90">
          The pattern where an AI tool searches a set of real documents first, then writes
          its answer using what it found. Perplexity, Google&apos;s AI overviews, and most
          modern research tools work this way. RAG is the AI-industry term for what you
          want when you research: model output that is tied to actual sources, not
          training-data guesses.
        </p>
        <p className="mt-3 text-sm text-muted">
          Bucket two in the next section is RAG. The reason it is your friend in
          research is that the citations point at real sources you can open. The reason
          it is not the whole answer is that the AI still chooses what to quote and how
          to summarize. Your job is to open the link.
        </p>
      </div>

      <Subhead n="3" title="The three things AI gives you" />
      <p>
        When you ask AI a research question, the answer comes from one of
        three places. AI almost never tells you which. You have to ask. The
        right move for each is different. Mix them up and you submit
        invented citations.
      </p>
      <ThreeBuckets />
      <p className="mt-3 text-sm text-muted">
        The move: for any claim that matters, ask AI directly which bucket it
        came from. Training, search, or filling a gap. AI will usually tell
        you. Then verify accordingly. If you cannot tell which bucket a claim
        is in, treat it as bucket three.
      </p>

      <Subhead n="4" title="The answer-shaped lie" />
      <p>
        The most dangerous AI output is the one that looks like research and
        is not. Not a wrong answer. A fake citation. The student in the cold
        open got burned by this. So will you, if you do not learn the shape.
      </p>
      <FakeShapes />
      <p className="mt-3 text-sm text-muted">
        Pattern across all three: the AI is filling a gap with text that
        sounds like the kind of thing a real source would say. The fix is
        the same in every case. Click the link. If there is no link, ask for
        one. If the link does not exist, the source does not exist.
      </p>
    </Section>
  );
}

function YourTurn() {
  return (
    <Section
      id="sec-your-turn"
      label="Your turn · Spot the source"
      eyebrow="Four AI responses to the same research question. Label each one."
    >
      <p>
        Question you asked AI: <span className="italic text-text">How many
        US high schoolers participate in a varsity sport, and is that
        number growing?</span> Below are four real-looking answers AI might
        give you. For each one, pick the right label and check yourself.
      </p>

      <SourceDrill
        n={1}
        excerpt={
          'According to the National Federation of State High School Associations 2023 Athletics Participation Survey, about 7.8 million students participated in high school sports during the 2022-23 school year. The NFHS publishes this survey annually at nfhs.org.'
        }
        correct="search"
        verdict="Current search with a citation. The NFHS Athletics Participation Survey is real, it is published annually, and the URL is checkable. This is the strongest research output AI can give you: named source, named publisher, link you can open. Your job: open it. Confirm the year. Confirm the number. Then cite the source, not AI."
      />

      <SourceDrill
        n={2}
        excerpt={
          'High school sports participation has been generally rising over the past two decades, though the pandemic caused a temporary dip. Most studies show that participation rebounded by the 2022-23 school year.'
        }
        correct="training"
        verdict={"Training-data guess. No source named, no link, no year on the data. Phrases like \u201cmost studies show\u201d and \u201cgenerally rising\u201d are the tell. The AI is pattern-matching from things it read during training. The general shape is probably right. The specific claim is not citable. You cannot put this in a paper without going to find an actual study."}
      />

      <SourceDrill
        n={3}
        excerpt={
          'A 2021 study by Dr. Jennifer Marsh at the University of Michigan published in the Journal of Adolescent Athletics found that 56.3 percent of high school students participated in at least one school-sponsored sport, with significant variation by school size.'
        }
        correct="fake"
        verdict="Answer-shaped lie. The structure is perfect: author plus institution plus year plus journal plus specific statistic. The Journal of Adolescent Athletics does not exist. Dr. Jennifer Marsh may or may not exist. The 56.3 percent is invented to two decimal places to feel real. This is the most dangerous output AI produces. Suspicious specificity, no link, no DOI. If you cited this in a paper, you would have invented a source. Ask AI for the URL. There will not be one."
      />

      <SourceDrill
        n={4}
        excerpt={
          'Based on data published by the NCAA, only about 7 percent of high school athletes go on to play in college, and less than 2 percent play at the Division I level. Source: ncaa.org/research.'
        }
        correct="real"
        verdict={"Real, verifiable source. The NCAA publishes this statistic. The page exists. The 7 percent and less than 2 percent figures are publicly cited and have been stable for years. Caveat: AI gave you a research page URL, not a deep link to the specific stat. Your move is to open ncaa.org/research, search for \u201cparticipation,\u201d and cite the actual page you find. The citation in your paper is the page, not AI."}
      />
    </Section>
  );
}

function QuickCheck() {
  const items = [
    {
      q: 'Define primary source in one sentence.',
      a: 'The original place a fact lives: the paper, the database, the press release, the court ruling, the person who said it. Not a summary of the original. Not AI explaining it.',
    },
    {
      q: 'What are the three things AI gives you when you ask a research question?',
      a: 'Things from its training data (often right, not citable on their own, never fresh). Things it found by searching (citable if the source is real and current). Things it made up to fill a gap (the danger; they look like the first two).',
    },
    {
      q: 'AI cites a study with author, year, journal, and a specific statistic, but no URL. What do you do?',
      a: 'Ask AI for the URL. If there is no URL, the source probably does not exist. Suspicious specificity with no link is the classic shape of an answer-shaped lie. Never cite something you have not opened.',
    },
    {
      q: 'Why is AI a librarian and not a textbook?',
      a: 'A librarian knows where the books are. A librarian does not write the books. AI can point you to real sources but cannot replace the sources themselves. If you walk out with only the librarian summary and no book, you did not research.',
    },
    {
      q: 'What is the one move that catches answer-shaped lies almost every time?',
      a: 'Click the link. If there is no link, ask for one. If the link does not exist, the source does not exist. Suspicious specificity plus no URL equals invented citation.',
    },
  ];

  return (
    <Section
      id="sec-quick-check"
      label="Quick check"
      eyebrow="Five questions. Answer first. Then reveal."
    >
      <div className="space-y-3">
        {items.map((it, i) => (
          <QuickCheckItem key={i} n={i + 1} q={it.q} a={it.a} />
        ))}
      </div>
    </Section>
  );
}

function LockCard() {
  return (
    <Section
      id="sec-lock-card"
      label="Lock card"
      eyebrow="Screenshot this. Run it any time research matters."
    >
      <div className="rounded-xl border border-amber/40 bg-gradient-to-br from-amber/[0.05] to-amber/[0.02] p-8" data-lockcard-body>
        <div className="flex items-baseline justify-between">
          <p className="font-mono text-[11px] uppercase tracking-widest text-amber">
            Lesson 3.2 · Lock card · The research step
          </p>
          <button className="text-xs text-muted hover:text-text">
            ⤓ Save image
          </button>
        </div>

        <p className="mt-4 text-lg leading-relaxed text-text">
          Ask AI where to look. Then go look. The citation in your paper is
          the thing AI pointed at, not AI.
        </p>

        <div className="mt-6 grid gap-3 sm:grid-cols-3">
          <BucketTile
            n="01"
            label="Training"
            q="Where AI learned it"
            sub="Often right in shape. Never citable on its own. Never fresh. Use it to find what to look up."
          />
          <BucketTile
            n="02"
            label="Search"
            q="Where AI found it just now"
            sub="Citable when the source is real and current. You still open it. The link is the proof, not the answer."
          />
          <BucketTile
            n="03"
            label="Made up"
            q="Where AI filled the gap"
            sub="The dangerous one. Looks like training or search. No link. Specific to two decimals. Click. Or do not cite."
          />
        </div>

        <div className="mt-6 rounded-lg border border-line bg-ink/60 p-5">
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
            The one move that catches almost everything
          </p>
          <p className="mt-2 text-sm leading-relaxed text-text/90">
            Click the link. If there is no link, ask for one. If the link
            does not exist, the source does not exist. Suspicious specificity
            with no URL is an invented citation in disguise.
          </p>
        </div>

        <p className="mt-6 text-sm italic text-muted">
          AI is a librarian. The books are the source. You still have to
          open the books.
        </p>
      </div>
    </Section>
  );
}

// ---------- Custom components ----------

function CitationGrid() {
  const cites = [
    {
      n: '01',
      author: 'Crawford, V.',
      title: 'Women in the Civil Rights Movement, 1941-1965',
      pub: 'Indiana University Press, 1990',
      real: true,
    },
    {
      n: '02',
      author: 'Collier-Thomas, B.',
      title: 'Sisters in the Struggle',
      pub: 'NYU Press, 2001',
      real: true,
    },
    {
      n: '03',
      author: 'Robnett, B.',
      title: 'How Long? How Long? African American Women in the Struggle',
      pub: 'Oxford University Press, 1997',
      real: true,
    },
    {
      n: '04',
      author: 'Henderson, M.',
      title: 'The Hidden Architects: Women Strategists in the SCLC',
      pub: 'Journal of Southern Studies, 2018',
      real: false,
    },
  ];
  return (
    <div className="my-6 overflow-hidden rounded-xl border border-line bg-surface">
      <div className="border-b border-line bg-elevated/40 px-5 py-2.5">
        <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
          What AI returned · Four citations
        </p>
      </div>
      <div className="grid divide-y divide-line">
        {cites.map((c) => (
          <div
            key={c.n}
            className="grid grid-cols-[40px_minmax(0,1fr)_140px] items-start gap-4 px-5 py-4"
          >
            <span className="font-mono text-[11px] uppercase tracking-widest text-amber">
              {c.n}
            </span>
            <div>
              <p className="text-sm text-text">{c.author}</p>
              <p className="mt-0.5 text-sm italic text-text/80">{c.title}</p>
              <p className="mt-0.5 text-xs text-muted">{c.pub}</p>
            </div>
            <span
              className={`inline-block w-fit shrink-0 rounded-md px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest ${
                c.real
                  ? 'bg-emerald-500/20 text-emerald-300'
                  : 'bg-rose-500/20 text-rose-300'
              }`}
            >
              {c.real ? 'Real paper' : 'Invented'}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function LibrarianFrame() {
  return (
    <div className="my-6 grid gap-4 lg:grid-cols-2">
      <div className="rounded-xl border border-rose-500/30 bg-rose-500/[0.04] p-5">
        <p className="font-mono text-[10px] uppercase tracking-widest text-rose-300">
          AI as a textbook · Wrong
        </p>
        <p className="mt-3 text-sm text-text/90">
          You ask AI a question. AI returns a confident paragraph. You write
          down what AI said. You cite AI. You move to the next question.
        </p>
        <p className="mt-3 text-sm text-text/80">
          What you have: a paragraph from a thing that cannot be cited and
          cannot be checked. The teacher reading your work cannot verify any
          of it because there is nothing to verify.
        </p>
      </div>
      <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/[0.04] p-5">
        <p className="font-mono text-[10px] uppercase tracking-widest text-emerald-300">
          AI as a librarian · Right
        </p>
        <p className="mt-3 text-sm text-text/90">
          You ask AI a question. AI suggests three places to look: a database,
          a paper, a credible website. You open the sources. You read them.
          You take notes from what you read, not from what AI told you was
          there.
        </p>
        <p className="mt-3 text-sm text-text/80">
          What you have: three real sources you have actually opened, with
          your own notes about what they say. You can defend every line in
          your paper because you read every line of your sources.
        </p>
      </div>
    </div>
  );
}

function ThreeBuckets() {
  const buckets = [
    {
      n: '01',
      label: 'Training data',
      tone: 'training',
      sub: 'Things AI learned from text on the internet at some point in the past',
      good: 'Good shape on common topics. Strong for definitions, frameworks, examples that have been written about a million times.',
      bad: 'Always stale. Cannot be cited on its own. Drifts on edge cases. Drifts on anything specific. Numbers get hazy.',
      move: 'Use it to figure out what to look up. Treat it like a tip from a smart friend, not a paper. Then go find the paper.',
    },
    {
      n: '02',
      label: 'Search results',
      tone: 'search',
      sub: 'Things AI found right now by running a web search and reading the page',
      good: 'Citable if the source is real and current. Often comes with a URL. Good for news, statistics, official numbers, anything that changes.',
      bad: 'AI can still summarize the source wrong. The link can be paywalled or fluff. AI sometimes searches a weak source.',
      move: 'Open the link. Confirm the claim is on the page. Cite the page, not AI. If the source is junk, ask for a better one.',
    },
    {
      n: '03',
      label: 'Made up',
      tone: 'fake',
      sub: 'Things AI invented to fill a gap because the prompt expected an answer',
      good: 'Reads exactly like the other two. Confident. Specific. Sometimes more specific than the truth, because the model is filling in detail.',
      bad: 'Wrong on facts. Wrong on people. Wrong on URLs. Often catches students who never check. This is the academic dishonesty trap.',
      move: 'When in doubt, treat any claim as this until proven otherwise. Ask AI directly which bucket the claim came from.',
    },
  ];
  return (
    <div className="my-6 space-y-3">
      {buckets.map((b) => {
        const toneClass =
          b.tone === 'training'
            ? 'border-amber/30 bg-amber/[0.03]'
            : b.tone === 'search'
            ? 'border-emerald-500/30 bg-emerald-500/[0.03]'
            : 'border-rose-500/30 bg-rose-500/[0.04]';
        const labelClass =
          b.tone === 'training'
            ? 'text-amber'
            : b.tone === 'search'
            ? 'text-emerald-300'
            : 'text-rose-300';
        return (
          <div
            key={b.n}
            className={`rounded-xl border ${toneClass} px-5 py-4`}
          >
            <div className="flex items-baseline gap-3">
              <span className="font-mono text-[11px] uppercase tracking-widest text-amber">
                {b.n}
              </span>
              <span
                className={`inline-block w-fit shrink-0 font-semibold ${labelClass}`}
              >
                {b.label}
              </span>
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted">
                {b.sub}
              </span>
            </div>
            <div className="mt-3 grid gap-2 text-sm text-text/90 sm:grid-cols-3">
              <p>
                <span className="font-mono text-[10px] uppercase tracking-widest text-emerald-300">
                  Strong at ·{' '}
                </span>
                {b.good}
              </p>
              <p>
                <span className="font-mono text-[10px] uppercase tracking-widest text-rose-300">
                  Weak at ·{' '}
                </span>
                {b.bad}
              </p>
              <p>
                <span className="font-mono text-[10px] uppercase tracking-widest text-amber">
                  Your move ·{' '}
                </span>
                {b.move}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function FakeShapes() {
  const shapes = [
    {
      label: 'Vague source',
      sub: 'No name attached',
      example:
        'Most studies show that screen time correlates with lower test scores.',
      tell: 'Which studies? Where? When? The phrase studies show is AI saying it read this in training somewhere and cannot tell you where. Not citable.',
      fix: 'Ask AI to name the studies with year and authors. If it cannot, do not use the claim.',
    },
    {
      label: 'Suspicious specificity',
      sub: 'Too perfect to be real',
      example:
        'A 2021 Stanford study by Dr. Kim et al. found a 23.7 percent reduction in student focus when phones were within reach.',
      tell: 'Specific author, specific year, specific institution, specific statistic to one decimal. No URL. This is the academic dishonesty trap. Real studies usually have rounder numbers or live behind links.',
      fix: 'Ask for the URL. If no URL, source does not exist. Walk away.',
    },
    {
      label: 'Orphan numbers',
      sub: 'Stats with no parent',
      example:
        'About 73 percent of high schoolers report feeling stressed by social media.',
      tell: 'Number sounds real. No source. No survey name. No year. The number could be from a real survey, a misremembered one, or invention. You cannot tell.',
      fix: 'Ask for the survey name, year, and sample size. If AI cannot give a name, the number is unciteable.',
    },
  ];
  return (
    <div className="my-6 space-y-3">
      {shapes.map((s, i) => (
        <div
          key={s.label}
          className="overflow-hidden rounded-xl border border-line bg-surface"
        >
          <div className="border-b border-line bg-elevated/40 px-5 py-2.5">
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <span className="font-mono text-[11px] uppercase tracking-widest text-amber">
                {String(i + 1).padStart(2, '0')}
              </span>
              <span className="font-semibold text-text">{s.label}</span>
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted">
                {s.sub}
              </span>
            </div>
          </div>
          <div className="space-y-3 px-5 py-4">
            <p className="rounded-md border border-rose-500/30 bg-rose-500/[0.04] px-3 py-2.5 text-sm italic text-text/90">
              {s.example}
            </p>
            <p className="text-sm text-text/90">
              <span className="font-mono text-[10px] uppercase tracking-widest text-rose-300">
                The tell ·{' '}
              </span>
              {s.tell}
            </p>
            <p className="text-sm text-text/90">
              <span className="font-mono text-[10px] uppercase tracking-widest text-emerald-300">
                The fix ·{' '}
              </span>
              {s.fix}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}

const SOURCE_OPTIONS = [
  { id: 'real', label: 'Real, verifiable source' },
  { id: 'search', label: 'Current search with a citation' },
  { id: 'training', label: 'Training-data guess' },
  { id: 'fake', label: 'Answer-shaped lie' },
];

function SourceDrill({
  n,
  excerpt,
  correct,
  verdict,
}: {
  n: number;
  excerpt: string;
  correct: string;
  verdict: string;
}) {
  const [picked, setPicked] = useState<string | null>(null);
  const isCorrect = picked === correct;
  const wasWrong = picked !== null && !isCorrect;
  const pickedOption = SOURCE_OPTIONS.find((o) => o.id === picked);
  const correctOption = SOURCE_OPTIONS.find((o) => o.id === correct);
  return (
    <div className="my-6 overflow-hidden rounded-xl border border-line bg-surface">
      <div className="border-b border-line bg-elevated/40 px-5 py-3">
        <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
          Drill {n} · AI returned
        </p>
        <p className="mt-1.5 text-sm italic text-text/90">{excerpt}</p>
      </div>
      <div className="space-y-3 px-5 py-5">
        <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
          What kind of source is this?
        </p>
        <div className="grid gap-2 sm:grid-cols-2">
          {SOURCE_OPTIONS.map((opt) => {
            const selected = picked === opt.id;
            const isRight = picked && correct === opt.id;
            const isWrong = picked && selected && correct !== opt.id;
            const borderClass = isRight
              ? 'border-emerald-500/50 bg-emerald-500/[0.05]'
              : isWrong
              ? 'border-rose-500/50 bg-rose-500/[0.05]'
              : selected
              ? 'border-amber bg-amber/[0.06]'
              : 'border-line bg-ink/40 hover:border-amber/50';
            return (
              <button
                key={opt.id}
                onClick={() => setPicked(opt.id)}
                className={`rounded-lg border px-4 py-2.5 text-left text-sm transition-colors ${borderClass}`}
              >
                <span
                  className={`font-mono text-[10px] uppercase tracking-widest ${
                    selected ? 'text-amber' : 'text-muted'
                  }`}
                >
                  Option · {opt.id}
                </span>
                <p className="mt-0.5 text-text/90">{opt.label}</p>
              </button>
            );
          })}
        </div>

        {wasWrong && (
          <div className="mt-3 rounded-md border border-rose-500/40 bg-rose-500/[0.06] px-4 py-3">
            <p className="font-mono text-[10px] uppercase tracking-widest text-rose-300">
              Not this one · {pickedOption?.label}
            </p>
            <p className="mt-1.5 text-sm text-text/85">
              That is not the right call on this source. See the correct
              answer below.
            </p>
          </div>
        )}
        {picked && (
          <div className="mt-3 rounded-md border border-emerald-500/30 bg-emerald-500/[0.04] px-4 py-3">
            <p className="font-mono text-[10px] uppercase tracking-widest text-emerald-300">
              {wasWrong ? 'The right call' : 'Correct'} · {correctOption?.label}
            </p>
            <p className="mt-1.5 text-sm text-text/90">{verdict}</p>
          </div>
        )}
      </div>
    </div>
  );
}

function BucketTile({
  n,
  label,
  q,
  sub,
}: {
  n: string;
  label: string;
  q: string;
  sub: string;
}) {
  return (
    <div className="rounded-lg border border-line bg-ink/60 p-5">
      <div className="flex items-baseline gap-3">
        <span className="font-mono text-[11px] uppercase tracking-widest text-amber">
          {n}
        </span>
        <span className="inline-block w-fit shrink-0 rounded-md bg-amber/20 px-2 py-0.5 font-mono text-[11px] uppercase tracking-widest text-amber">
          {label}
        </span>
      </div>
      <p className="mt-3 text-base font-semibold text-text">{q}</p>
      <p className="mt-2 text-sm leading-relaxed text-text/90">{sub}</p>
    </div>
  );
}

function NextUp() {
  return (
    <div className="mt-12 rounded-lg border border-line bg-surface p-6" data-nextup-card>
      <p className="font-mono text-[11px] uppercase tracking-widest text-muted">
        Up next · Lesson 3.3
      </p>
      <div className="mt-2 flex items-start justify-between gap-6">
        <div>
          <h3 className="text-xl font-semibold text-text">
            The draft step: getting words on the page
          </h3>
          <p className="mt-2 text-sm text-muted">
            Second link in the chain. Where the actual writing happens. When
            AI drafts for you, when you draft for AI, and the moves that keep
            the words yours either way.
          </p>
        </div>
        <button className="shrink-0 rounded-md border border-line px-4 py-2 text-sm text-muted hover:border-amber hover:text-text">
          Coming soon
        </button>
      </div>
    </div>
  );
}

// ---------- Shared primitives ----------

function Subhead({ n, title }: { n: string; title: string }) {
  return (
    <h3 className="mt-8 flex items-baseline gap-3 text-lg font-semibold text-text">
      <span className="font-mono text-[11px] uppercase tracking-widest text-amber">
        {n}
      </span>
      {title}
    </h3>
  );
}

function VocabCard({
  term,
  definition,
  analogy,
  hook,
  matters,
}: {
  term: string;
  definition: string;
  analogy: string;
  hook: string;
  matters: string;
}) {
  const [draft, setDraft] = useState('');
  const [revealed, setRevealed] = useState(false);
  return (
    <div className="my-6 overflow-hidden rounded-xl border border-line bg-surface">
      <div className="border-b border-line bg-elevated/40 px-5 py-3">
        <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
          Vocab · 3.2 · New term
        </p>
        <h4 className="mt-1 text-xl font-semibold text-text">{term}</h4>
      </div>
      <div className="space-y-4 px-5 py-5">
        {!revealed && (
          <>
            <p className="text-sm text-muted">
              Write your own one-sentence definition first. Then reveal.
            </p>
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={`Your definition of "${term}"...`}
              className="h-20 w-full resize-none rounded-md border border-line bg-ink/60 px-3 py-2 text-sm text-text placeholder:text-muted/60 focus:border-amber focus:outline-none"
            />
            <button
              onClick={() => setRevealed(true)}
              className="rounded-md bg-amber px-4 py-2 text-xs font-semibold text-ink hover:bg-amber-hover"
            >
              Reveal definition →
            </button>
          </>
        )}
        {revealed && (
          <div className="space-y-3">
            <Block label="Definition" body={definition} />
            <Block label="Analogy" body={analogy} accent="amber" />
            <Block label="Memory hook" body={hook} />
            <Block label="Why it matters" body={matters} />
          </div>
        )}
      </div>
    </div>
  );
}

function Block({
  label,
  body,
  accent = 'plain',
}: {
  label: string;
  body: string;
  accent?: 'plain' | 'amber';
}) {
  return (
    <div
      className={`rounded-md border px-4 py-3 ${
        accent === 'amber'
          ? 'border-amber/40 bg-amber/[0.04]'
          : 'border-line bg-ink/40'
      }`}
    >
      <p
        className={`font-mono text-[10px] uppercase tracking-widest ${
          accent === 'amber' ? 'text-amber' : 'text-muted'
        }`}
      >
        {label}
      </p>
      <p className="mt-1.5 text-sm leading-relaxed text-text/90">{body}</p>
    </div>
  );
}

function QuickCheckItem({ n, q, a }: { n: number; q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-line bg-surface">
      <div className="flex items-start gap-3 p-5">
        <span className="mt-0.5 font-mono text-[11px] uppercase tracking-widest text-amber">
          Q{n}
        </span>
        <div className="flex-1">
          <p className="text-text">{q}</p>
          {open && (
            <div className="mt-3 rounded-md border border-amber/30 bg-amber/[0.04] p-3 text-sm text-text">
              <p className="font-mono text-[10px] uppercase tracking-widest text-amber">
                Model answer
              </p>
              <p className="mt-1.5">{a}</p>
            </div>
          )}
        </div>
        <button
          onClick={() => setOpen((v) => !v)}
          className="rounded-md border border-line px-3 py-1.5 text-xs text-muted hover:border-amber hover:text-text"
        >
          {open ? 'Hide' : 'Reveal'}
        </button>
      </div>
    </div>
  );
}

function Section({
  id,
  label,
  eyebrow,
  accent = 'plain',
  children,
}: {
  id: string;
  label: string;
  eyebrow: string;
  accent?: 'amber' | 'blue' | 'rose' | 'plain';
  children: React.ReactNode;
}) {
  const colorMap = {
    amber: 'text-amber',
    blue: 'text-[#7DC8E8]',
    rose: 'text-[#E89F8B]',
    plain: 'text-text',
  };
  return (
    <section
      id={id}
      className="scroll-mt-24 border-t border-line py-12 first-of-type:border-t-0 first-of-type:pt-0"
    >
      <div className="mb-6">
        <p
          className={`font-mono text-[11px] uppercase tracking-[0.18em] ${colorMap[accent]}`}
        >
          {label}
        </p>
        <p className="mt-1.5 text-sm text-muted">{eyebrow}</p>
      </div>
      <div className="space-y-4 text-base leading-relaxed text-text/90">
        {children}
      </div>
    </section>
  );
}

function SectionNav({ activeIndex }: { activeIndex: number }) {
  const items = [
    { id: 'sec-cold-open', label: 'Cold open' },
    { id: 'sec-from-last-time', label: '3.1 review' },
    { id: 'sec-teaching', label: 'Research, real version' },
    { id: 'sec-your-turn', label: 'Your turn' },
    { id: 'sec-quick-check', label: 'Quick check' },
    { id: 'sec-lock-card', label: 'Lock card' },
  ];
  return (
    <nav>
      <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
        In this lesson
      </p>
      <ul className="mt-4 space-y-1">
        {items.map((it, i) => {
          const active = i === activeIndex;
          const done = i < activeIndex;
          return (
            <li key={it.id}>
              <a
                href={`#${it.id}`}
                className={`flex items-center gap-3 rounded-md px-2.5 py-2 text-sm transition-colors ${
                  active
                    ? 'bg-elevated text-text'
                    : 'text-muted hover:bg-elevated/60 hover:text-text'
                }`}
              >
                <span
                  className={`flex h-5 w-5 items-center justify-center rounded-full border font-mono text-[10px] ${
                    active
                      ? 'border-amber bg-amber text-ink'
                      : done
                      ? 'border-amber/40 text-amber'
                      : 'border-line text-muted'
                  }`}
                >
                  {done ? '✓' : i + 1}
                </span>
                {it.label}
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

function ClockIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}
